/**
 * Saved-tracks library — named own-ship tracks kept ON the device (localStorage),
 * so they survive reloads. Mirrors the route/pilotage managers: a list with
 * rename, delete, show/hide on the chart, and per-track GPX export. The LIVE
 * recording track stays in the chart-plotter store; "Save" snapshots it into
 * here. Saved tracks draw as faded 'secondary' tracks under the live one.
 */
import { create } from 'zustand';
import type { TrackPoint } from '@/ownship';

export interface SavedTrack {
  id: string;
  name: string;
  points: TrackPoint[];
  /** Unix ms when it was saved. */
  savedAt: number;
}

const KEY = 'acp.savedTracks.v1';

function loadPersisted(): { tracks: SavedTrack[]; hiddenIds: string[] } {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return { tracks: [], hiddenIds: [] };
    const d = JSON.parse(raw) as { tracks?: unknown; hiddenIds?: unknown };
    return {
      tracks: Array.isArray(d.tracks) ? (d.tracks as SavedTrack[]) : [],
      hiddenIds: Array.isArray(d.hiddenIds) ? (d.hiddenIds as string[]) : [],
    };
  } catch {
    return { tracks: [], hiddenIds: [] };
  }
}

function persist(tracks: SavedTrack[], hiddenIds: string[]): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(KEY, JSON.stringify({ tracks, hiddenIds }));
    }
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

/** A readable default name from the time, e.g. "Track 2026-06-15 14:30". */
export function defaultTrackName(when = new Date()): string {
  const iso = new Date(when.getTime() - when.getTimezoneOffset() * 60000).toISOString();
  return `Track ${iso.slice(0, 16).replace('T', ' ')}`;
}

let seq = 1;
function nextSavedId(): string {
  return `st-${Date.now().toString(36)}-${seq++}`;
}

interface SavedTracksState {
  tracks: SavedTrack[];
  /** Saved-track ids hidden from the chart. */
  hiddenIds: string[];
  /** Snapshot points into a named saved track; returns its id, or null if <2 points. */
  saveTrack: (points: TrackPoint[], name?: string) => string | null;
  renameTrack: (id: string, name: string) => void;
  removeTrack: (id: string) => void;
  toggleHidden: (id: string) => void;
}

export const useSavedTracksStore = create<SavedTracksState>((set, get) => {
  const init = loadPersisted();
  return {
    tracks: init.tracks,
    hiddenIds: init.hiddenIds,
    saveTrack: (points, name) => {
      if (points.length < 2) return null;
      const t: SavedTrack = {
        id: nextSavedId(),
        name: name?.trim() || defaultTrackName(),
        points: points.map((p) => ({ ...p })),
        savedAt: Date.now(),
      };
      const tracks = [...get().tracks, t];
      persist(tracks, get().hiddenIds);
      set({ tracks });
      return t.id;
    },
    renameTrack: (id, name) => {
      const tracks = get().tracks.map((t) => (t.id === id ? { ...t, name } : t));
      persist(tracks, get().hiddenIds);
      set({ tracks });
    },
    removeTrack: (id) => {
      const tracks = get().tracks.filter((t) => t.id !== id);
      const hiddenIds = get().hiddenIds.filter((x) => x !== id);
      persist(tracks, hiddenIds);
      set({ tracks, hiddenIds });
    },
    toggleHidden: (id) => {
      const h = get().hiddenIds;
      const hiddenIds = h.includes(id) ? h.filter((x) => x !== id) : [...h, id];
      persist(get().tracks, hiddenIds);
      set({ hiddenIds });
    },
  };
});
